import { useCallback, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	isImageCreateData,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	getRemixMetadata,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { duplicateMauiApp } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { MessageActionPayload } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useChatStoreActions } from '@atlassian/conversation-assistant/controllers/chat-store';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { useEditorRemixContext } from './context';
import {
	getRemixIdFromMessage,
	getRemixUrl,
	getMauiUrl,
	getStreamingWhiteboardEmbedUrl,
	getWhiteboardCreateActionData,
	insertEmbedIntoPage,
	sendWhiteboardStreamCompleteCommand,
	needsReparenting,
	getReparentContentType,
	FINALIZABLE_CONTENT_ACTION_KEYS,
	fetchInfographicUrl,
	isPageLevelRemix,
} from './utils/message';
import { getMauiIdFromUrl } from './utils/remix-ids';

const isFinalizePermissionError = (error: Error): boolean =>
	(error as Error & { errorAttributes?: { statusCode?: number } }).errorAttributes?.statusCode ===
	403;

export function useOnComplete(): void {
	const {
		isViewMode,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		dynamicUiType,
		dynamicUiSubtype: dynamicUiSubtypeFromContext,
		dynamicUiSource,
		conversationId,
		interactionSource,
		aspectRatio,
		finalMessage,
		contentClassification,
		clearThinkingDecoration,
		replaceWhiteboardEmbed,
		setThinkingComplete,
		finishRemixThinking,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onVariantUpdateComplete,
		smartCreationConfig = { siteId: '', product: '', userId: '' },
		smartCreationContext,
		resetContext,
	} = useEditorRemixContext();
	const experienceTracker = useAIMateExperienceTracker();
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const { resolveConversationAction } = useChatStoreActions();

	const { userId: clientId, siteId } = smartCreationConfig;
	const action = finalMessage?.content?.message?.message?.actions?.[0];
	const actionData = action?.data;
	const actionKey = action?.key;
	const remixId = getRemixIdFromMessage({ message: finalMessage });
	const agentMessageId = finalMessage?.content?.message?.message?.id;
	const mediaFileId = isImageCreateData(actionData) ? actionData.mediaFileId : undefined;
	const backendDynamicUiSubtype = (actionData as { dynamicUiSubtype?: string } | undefined)
		?.dynamicUiSubtype;
	// Repro: choosing "Pick for me" starts without a subtype in context; use
	// the backend-confirmed subtype from the final action so history reflects it.
	const dynamicUiSubtype = dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype;
	const isLiveDoc = smartCreationContext?.confluence?.content?.subType === 'live';
	const contentType = smartCreationContext?.confluence?.content?.type;
	const currentPageId = smartCreationContext?.confluence?.content?.id;
	const spaceId = smartCreationContext?.confluence?.space?.id;
	const spaceKey = smartCreationContext?.confluence?.space?.key;
	const remixUrl = getRemixUrl(remixId, actionKey, spaceId, spaceKey);
	const finalWhiteboardCreateActionData = getWhiteboardCreateActionData(finalMessage);
	// For non-Confluence products, `context.editor` provides a pre-formed containerAri
	// for the duplicate remix API call and an editorId for routing the insert command
	// to the correct editor instance.
	const smartCreationEditorContext = smartCreationContext?.editor;
	// The page/editor ID used to route EditorInsertContentCommand via the rovo content bridge.
	// Prefers the Confluence content ID; falls back to the editor UUID for non-Confluence products.
	const insertReceiverId = currentPageId ?? smartCreationEditorContext?.editorId;

	/**
	 * Calls the convo-ai action endpoint when `aifc_finalize_convo_ai` is on.
	 * `onError` re-throws so callers can detect failure — without it, the chat
	 * store's safe-action wrapper would silently swallow the error.
	 */
	const finalizeAction = useCallback(
		async ({
			contentId,
			parentId,
			resolvedSpaceId,
		}: {
			contentId: string;
			parentId: string | undefined;
			resolvedSpaceId: string | undefined;
		}): Promise<void> => {
			if (!conversationId) {
				return;
			}

			// Only finalize via convo-ai for Confluence object types (pages, whiteboards,
			// databases). MAUI apps and images don't need this endpoint.
			if (!actionKey || !FINALIZABLE_CONTENT_ACTION_KEYS.has(actionKey)) {
				return;
			}

			const message = finalMessage?.content?.message?.message;
			const messageAction = message?.actions?.[0];
			if (!message || !messageAction) {
				return;
			}

			const isActionComplete =
				finalMessage?.content.streamingComplete ||
				messageAction?.status?.toLowerCase() === 'planned' ||
				messageAction?.status?.toLowerCase() === 'finished';

			if (!isActionComplete) {
				return;
			}

			const existingData = (messageAction.data as Record<string, unknown>) ?? {};
			const existingMetadata = (existingData.metadata as Record<string, unknown>) ?? {};

			const payload: MessageActionPayload = {
				context: {
					key: messageAction.key,
					invocationId: messageAction.invocationId,
					actionId: messageAction.actionId,
					displayName: messageAction.displayName,
					status: messageAction.status,
					toolSource: messageAction.toolSource,
					humanMessageId: messageAction.humanMessageId,
					confirmationMessage: messageAction.confirmationMessage,
					executedData: messageAction.executedData,
					messageId: message.id,
					resolution: {
						type: 'confirm',
						message: 'finalize',
					},
					data: {
						...existingData,
						metadata: {
							...existingMetadata,
							contentId,
							parentId,
							spaceId: resolvedSpaceId,
						},
					},
				},
			};

			await resolveConversationAction({
				conversationId,
				messageId: message.id,
				payload,
				actionContentProcessor: (msg) => ({
					message: { message_metadata: msg?.message_metadata },
				}),
				onError: ({ error }) => {
					throw error;
				},
			});
		},
		[finalMessage, resolveConversationAction, conversationId, actionKey],
	);

	const clearThinkingDecorationRef = useRef(clearThinkingDecoration);
	useEffect(() => {
		clearThinkingDecorationRef.current = clearThinkingDecoration;
	}, [clearThinkingDecoration]);

	const replaceWhiteboardEmbedRef = useRef(replaceWhiteboardEmbed);
	useEffect(() => {
		replaceWhiteboardEmbedRef.current = replaceWhiteboardEmbed;
	}, [replaceWhiteboardEmbed]);

	const setThinkingCompleteRef = useRef(setThinkingComplete);
	useEffect(() => {
		setThinkingCompleteRef.current = setThinkingComplete;
	}, [setThinkingComplete]);

	const finishRemixThinkingRef = useRef(finishRemixThinking);
	useEffect(() => {
		finishRemixThinkingRef.current = finishRemixThinking;
	}, [finishRemixThinking]);

	const hasInsertedRef = useRef(false);
	const hasEarlyInsertedWhiteboardRef = useRef(false);
	const earlyInsertedWhiteboardLocalIdRef = useRef<string | undefined>(undefined);
	const earlyInsertedWhiteboardUrlRef = useRef<string | undefined>(undefined);

	const insertEarlyStreamingWhiteboard = useCallback(
		(contentId: string) => {
			const earlyRemixUrl = getStreamingWhiteboardEmbedUrl(contentId);
			const localId = crypto.randomUUID();

			hasEarlyInsertedWhiteboardRef.current = true;
			earlyInsertedWhiteboardLocalIdRef.current = localId;
			earlyInsertedWhiteboardUrlRef.current = earlyRemixUrl;
			insertEmbedIntoPage({
				url: earlyRemixUrl,
				insertReceiverId,
				isUpdate,
				localId,
			});

			// The whiteboard iframe is now the streaming surface. Hide only the editor-owned
			// generating decoration; keep the Remix flow mounted so finalMessage can still
			// trigger finalize/reparent and history recording.
			clearThinkingDecorationRef.current?.();
		},
		[insertReceiverId, isUpdate],
	);

	const replaceEarlyStreamingWhiteboard = useCallback((contentId: string, finalizedUrl: string) => {
		replaceWhiteboardEmbedRef.current?.({
			contentId,
			finalizedUrl,
			localId: earlyInsertedWhiteboardLocalIdRef.current,
		});
	}, []);

	const handleInsertMauiEmbedIntoPage = useCallback(async () => {
		let url = '';

		if (!conversationId || !remixId || !remixUrl) {
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT,
			id: ExperienceName.REMIX_INSERT,
			attributes: {
				actionKey,
				dynamicUiType,
				contentType: getReparentContentType(actionKey),
				conversationChannelId: conversationId,
			},
		});

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: getReparentContentType(actionKey),
				createdContentMetadata: {
					remixId,
					contentId: currentPageId,
					spaceId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype,
				userAction: 'insert',
			},
		});

		try {
			// For MAUI apps, duplicate the app first, then insert the duplicated URL.
			// When context.editor is set (gated by platform_editor_maui_jira_updates), use its
			// pre-formed containerAri. Otherwise build a Confluence ARI from the page ID.
			if (actionKey === DYNAMIC_UI_CREATE_KEY && (currentPageId || smartCreationEditorContext)) {
				const duplicateExperienceAttributes = {
					appId: remixId,
					currentPageId,
					source: 'mauiEmbedFinalizeButtons',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_DUPLICATE_APP,
					id: ExperienceName.REMIX_DUPLICATE_APP,
					attributes: duplicateExperienceAttributes,
				});

				let duplicateFailed = false;
				try {
					const { id: newAppId } = await duplicateMauiApp(
						remixId,
						smartCreationEditorContext
							? { containerAri: smartCreationEditorContext.containerAri }
							: {
									containerContentId: currentPageId ?? '',
									cloudId: smartCreationConfig.siteId,
									contentType,
								},
					);
					const duplicatedUrl = getMauiUrl(newAppId);
					insertEmbedIntoPage({ url: duplicatedUrl, insertReceiverId, isUpdate });
					url = duplicatedUrl;

					sendAnalyticsEvent({
						eventType: 'operational',
						actionSubject: 'mauiAppDuplicate',
						action: 'succeeded',
						actionSubjectId: 'mauiEmbedInsertIntoPage',
						attributes: {
							originalId: remixId,
							newId: newAppId,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						attributes: { ...duplicateExperienceAttributes, newAppId },
					});
				} catch (error) {
					duplicateFailed = true;
					sendAnalyticsEvent({
						eventType: 'operational',
						actionSubject: 'mauiAppDuplicate',
						action: 'failed',
						actionSubjectId: 'mauiEmbedInsertIntoPage',
						attributes: {
							originalId: remixId,
						},
					});

					const duplicateError =
						error instanceof Error ? error : new Error('Failed to duplicate MAUI app');
					experienceTracker.fail({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						error: duplicateError,
						attributes: duplicateExperienceAttributes,
					});
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: duplicateError,
						attributes: {
							...duplicateExperienceAttributes,
							failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
						},
					});

					// Duplication failed — insert the original URL as a fallback
					// so the user isn't left with nothing.
					insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
					url = remixUrl;
				}

				if (!duplicateFailed) {
					experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				}
				return url;
			}

			if (!needsReparenting(actionKey)) {
				// Fallback for MAUI apps reached without a currentPageId (the
				// duplicate flow above requires one). Insert the original URL
				// as-is.
				insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
				url = remixUrl;
				try {
					await finalizeAction({
						contentId: remixId,
						parentId: currentPageId,
						resolvedSpaceId: spaceId,
					});
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: error instanceof Error ? error : new Error('finalizeAction failed'),
					});
					return;
				}
				experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				return url;
			}

			// Whiteboards must be durable before the page keeps a final embed URL. If we
			// inserted a temporary streaming URL on the first chunk, finalization still runs
			// here and then swaps that exact node to the canonical finalized URL by localId.
			let finalizeError: Error | undefined;
			try {
				await finalizeAction({
					contentId: remixId,
					parentId: currentPageId,
					resolvedSpaceId: spaceId,
				});
			} catch (error) {
				finalizeError = error instanceof Error ? error : new Error('Failed to finalize whiteboard');
			}

			if (!finalizeError) {
				if (hasEarlyInsertedWhiteboardRef.current) {
					replaceEarlyStreamingWhiteboard(remixId, remixUrl);
				} else {
					insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
				}
				url = remixUrl;
				experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				return url;
			}

			if (hasEarlyInsertedWhiteboardRef.current && isFinalizePermissionError(finalizeError)) {
				experienceTracker.succeed({
					name: ExperienceName.REMIX_INSERT,
					attributes: { isFinalizePermissionError: true },
				});
			} else {
				experienceTracker.fail({
					name: ExperienceName.REMIX_INSERT,
					error: finalizeError,
				});
			}

			if (hasEarlyInsertedWhiteboardRef.current) {
				return earlyInsertedWhiteboardUrlRef.current;
			}
		} catch (error) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_INSERT,
				error: error instanceof Error ? error : new Error('Failed to insert into page'),
			});
		}
	}, [
		conversationId,
		contentType,
		remixUrl,
		remixId,
		actionKey,
		isLiveDoc,
		spaceId,
		agentMessageId,
		smartCreationEditorContext,
		currentPageId,
		dynamicUiType,
		dynamicUiSubtype,
		insertReceiverId,
		finalizeAction,
		experienceTracker,
		trackAIMAUEvent,
		sendAnalyticsEvent,
		smartCreationConfig.siteId,
		isUpdate,
		replaceEarlyStreamingWhiteboard,
	]);

	const handleInsertInfographicIntoPage = useCallback(async () => {
		if (!mediaFileId || !clientId || !insertReceiverId || !siteId) {
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT,
			id: ExperienceName.REMIX_INSERT,
			attributes: {
				actionKey,
				dynamicUiType,
				contentType: 'image',
				conversationChannelId: conversationId,
			},
		});

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: 'image',
				createdContentMetadata: {
					mediaFileId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype,
				userAction: 'insert',
			},
		});

		try {
			const mediaUrl = await fetchInfographicUrl({
				mediaFileId,
				clientId,
				product: 'confluence',
			});

			getBrowserBridgeAPI().send(EditorInsertContentCommand, {
				senderId: ROVO_CHAT_CLIENT_ID,
				receiverId: getConfluenceEditorClientId(insertReceiverId),
				mode: 'upload-and-insert-media',
				placement: isPageLevelRemix(dynamicUiSource)
					? 'end-of-doc'
					: isUpdate && fg('platform_editor_ai_remix_toolbar')
						? 'replace-selection'
						: 'after-block',
				useStoredSelection: true,
				conversationChannelId: conversationId,
				interactionSource,
				aspectRatio,
				remixSubtype: dynamicUiSubtype,
				data: {
					type: 'image',
					url: mediaUrl,
					presentation: undefined,
				},
			});

			experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
		} catch (error) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_INSERT,
				error: error instanceof Error ? error : new Error('Failed to insert infographic into page'),
			});
			return;
		}
	}, [
		mediaFileId,
		clientId,
		siteId,
		dynamicUiType,
		actionKey,
		experienceTracker,
		conversationId,
		dynamicUiSource,
		trackAIMAUEvent,
		agentMessageId,
		isLiveDoc,
		insertReceiverId,
		dynamicUiSubtype,
		isUpdate,
		interactionSource,
		aspectRatio,
	]);

	const handleInsertMauiEmbedIntoPageRef = useRef(handleInsertMauiEmbedIntoPage);
	useEffect(() => {
		handleInsertMauiEmbedIntoPageRef.current = handleInsertMauiEmbedIntoPage;
	}, [handleInsertMauiEmbedIntoPage]);

	const handleInsertInfographicIntoPageRef = useRef(handleInsertInfographicIntoPage);
	useEffect(() => {
		handleInsertInfographicIntoPageRef.current = handleInsertInfographicIntoPage;
	}, [handleInsertInfographicIntoPage]);

	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (isViewMode) {
			return;
		}
		if (hasEarlyInsertedWhiteboardRef.current) {
			return;
		}
		if (contentClassification?.type !== 'whiteboard' || !contentClassification.data?.contentId) {
			return;
		}

		// Insert the temporary whiteboard shell when the first real ACTION_PART arrives.
		// This avoids showing an empty whiteboard during long gaps between classification
		// and the first streamed diagram content. The same localId is later used to replace
		// this temporary URL with the finalized canonical URL, even if native-embeds has
		// transformed the Smart Link into a native whiteboard extension in the meantime.
		insertEarlyStreamingWhiteboard(contentClassification.data.contentId);
	}, [contentClassification, insertEarlyStreamingWhiteboard, isViewMode]);

	useEffect(() => {
		if (isViewMode) {
			return;
		}

		if (
			actionKey &&
			finalMessage &&
			!hasInsertedRef.current &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			hasInsertedRef.current = true;

			const insertContent = async () => {
				if (actionKey === IMAGE_CREATE_KEY) {
					await handleInsertInfographicIntoPageRef.current();
					// for image, the real fieldId will be updated after the image is uploaded via updateRemixImageHistoryFieldId
					return { fieldId: 'fieldId-placeholder' };
				} else {
					const url = await handleInsertMauiEmbedIntoPageRef.current();

					if (hasEarlyInsertedWhiteboardRef.current) {
						// The ephemeral preview sends a final whiteboard bridge command with
						// `isStreamComplete: true`; the whiteboard client uses that final signal
						// for end-of-stream behaviours such as zoom-to-fit. Since the editor flow
						// inserts the whiteboard outside the preview, mirror that signal here once
						// streaming has completed, even when finalization fails and the inserted
						// diagram remains unfinalized.
						sendWhiteboardStreamCompleteCommand({
							data: finalWhiteboardCreateActionData,
							groupId: conversationId,
							singleInstrumentationID: agentMessageId,
						});
					}

					return { url };
				}
			};

			insertContent()
				.then((result) => {
					if (!result) {
						return;
					}
					const { fieldId, url } = result;
					if (!isUpdate) {
						onRemixInsertSuccess?.({
							currentConversationId: conversationId,
							remixMetadata: getRemixMetadata({
								action,
								selectedRemixMetadata: {
									selectedDynamicUiType: dynamicUiType,
									selectedDynamicUiSubtype: dynamicUiSubtype,
								},
							}),
						});
					}
					if (!fg('platform_editor_ai_remix_toolbar')) {
						return;
					}
					if (hasEarlyInsertedWhiteboardRef.current && url) {
						finishRemixThinkingRef.current?.();
					}
					if (isUpdate) {
						if (conversationId && historyEntryForUpdate?.dynamicUiType) {
							addHistoryStackEntry?.({
								conversationId,
								dynamicUiType: historyEntryForUpdate.dynamicUiType,
								dynamicUiSubtype: dynamicUiSubtypeForUpdate,
								finalMessage,
								previousFieldId: historyEntryForUpdate.fieldId,
								previousUrl: historyEntryForUpdate.url,
								url,
								fieldId,
							});
						}
						// Release the in-flight mark so the bridge processors resume
						// handling BE pushes for this id. Must match the toolbar's
						// tracking key: image fieldId, or MAUI app id from the
						// *pre-update* stack-entry URL.
						const completionId =
							historyEntryForUpdate?.fieldId ?? getMauiIdFromUrl(historyEntryForUpdate?.url);
						if (completionId) {
							onVariantUpdateComplete?.(completionId);
						}
					} else {
						if (conversationId && dynamicUiSource && dynamicUiType) {
							addHistoryEntry?.({
								conversationId,
								dynamicUiSource,
								dynamicUiSubtype,
								dynamicUiType,
								finalMessage,
								contentClassification:
									contentClassification?.type === 'whiteboard'
										? { ...contentClassification, data: finalWhiteboardCreateActionData }
										: contentClassification,
								url,
								fieldId,
							});
						}
					}
				})
				.finally(() => {
					resetContext();
					setThinkingCompleteRef.current?.(true);
				});
		}
	}, [
		isViewMode,
		action,
		actionKey,
		finalMessage,
		resetContext,
		conversationId,
		dynamicUiType,
		dynamicUiSource,
		dynamicUiSubtype,
		contentClassification,
		finalWhiteboardCreateActionData,
		agentMessageId,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onVariantUpdateComplete,
		isUpdate,
		historyEntryForUpdate?.dynamicUiType,
		dynamicUiSubtypeForUpdate,
		historyEntryForUpdate,
	]);

	// reset the hasInsertedRef when the final message is cleared
	useEffect(() => {
		if (!finalMessage && fg('platform_editor_ai_remix_in_editor_loading')) {
			hasInsertedRef.current = false;
			hasEarlyInsertedWhiteboardRef.current = false;
			earlyInsertedWhiteboardLocalIdRef.current = undefined;
			earlyInsertedWhiteboardUrlRef.current = undefined;
		}
	}, [finalMessage]);
}
